/** Persisted shape for the reusable `unit_value` property type. */
export type UnitValue = {
  unit: string;
  value: number;
};

/** Choice shown in the unit dropdown. */
export type UnitValueChoice = {
  readonly value: string;
  readonly label: string;
  /** When true, the numeric input is disabled (e.g. `auto`). */
  readonly inputDisabled?: boolean;
};

export const EMPTY_UNIT_VALUE: UnitValue = {
  unit: 'px',
  value: 0,
};

export function normalizeUnitValue(
  value: unknown,
  fallback: UnitValue = EMPTY_UNIT_VALUE,
): UnitValue {
  if (typeof value === 'string') {
    return parseUnitValueString(value, fallback);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { unit: fallback.unit, value };
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...fallback };
  }
  const record = value as Record<string, unknown>;
  const unit =
    typeof record['unit'] === 'string' && record['unit'].trim()
      ? record['unit'].trim()
      : fallback.unit;
  const raw = record['value'];
  const numeric =
    typeof raw === 'number' && Number.isFinite(raw)
      ? raw
      : typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))
        ? Number(raw)
        : fallback.value;
  return { unit, value: numeric };
}

export function isUnitInputDisabled(
  unit: string,
  choices: readonly UnitValueChoice[] | undefined,
): boolean {
  const match = choices?.find(choice => choice.value === unit);
  return match?.inputDisabled === true;
}

/**
 * Serializes a unit value to a CSS-like string (`auto`, `16px`, `50%`, …).
 * Units with `inputDisabled` (or without a numeric contribution) emit the unit alone.
 */
export function serializeUnitValue(
  value: unknown,
  choices?: readonly UnitValueChoice[],
): string {
  const normalized = normalizeUnitValue(value);
  if (isUnitInputDisabled(normalized.unit, choices)) {
    return normalized.unit;
  }
  const amount = Number.isFinite(normalized.value) ? String(normalized.value) : '0';
  return `${amount}${normalized.unit}`;
}

/** Best-effort parse of legacy plain strings (`auto`, `16`, `16px`, `1.5rem`). */
export function parseUnitValueString(
  raw: string,
  fallback: UnitValue = EMPTY_UNIT_VALUE,
): UnitValue {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { ...fallback };
  }
  if (/^[a-zA-Z%-]+$/.test(trimmed)) {
    return { unit: trimmed.toLowerCase() === 'auto' ? 'auto' : trimmed, value: fallback.value };
  }
  const match = /^(-?\d+(?:\.\d+)?)([a-zA-Z%]*)$/.exec(trimmed);
  if (!match) {
    return { ...fallback };
  }
  const amount = Number(match[1]);
  const unit = match[2] && match[2].length > 0 ? match[2] : fallback.unit;
  return {
    unit,
    value: Number.isFinite(amount) ? amount : fallback.value,
  };
}
